// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { ApiError } from "@bitebalance/contracts/client"
import { ErrorEnvelopeSchema } from "@bitebalance/contracts"

/** What a web-only client extension needs to speak to the same API as the generated client. */
export interface WebRequestDeps {
  baseUrl: string
  getToken: () => string | Promise<string>
  fetchImpl?: typeof fetch
  defaultHeaders?: Record<string, string>
}

/**
 * ONE transport for the web-only GET extensions (`getRecipe(id, {servings})`,
 * `getConsumptionHistory({from, to})`): the SAME base URL, bearer token,
 * default headers and error-envelope handling as `@bitebalance/contracts`'
 * generated client, with the response validated against the contract
 * schema before it is handed to the app — a contract drift fails loudly at
 * the client boundary instead of rendering bad data.
 */
export async function webGet<T>(
  deps: WebRequestDeps,
  path: string,
  query: Record<string, string>,
  schema: { parse: (input: unknown) => T }
): Promise<T> {
  const baseUrl = deps.baseUrl.replace(/\/$/, "")
  const search = new URLSearchParams(query).toString()
  const url = `${baseUrl}${path}${search ? `?${search}` : ""}`
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...deps.defaultHeaders,
  }
  const token = await deps.getToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const doFetch = deps.fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init))
  const res = await doFetch(url, { method: "GET", headers })
  const json: unknown = res.status === 204 ? undefined : await res.json().catch(() => undefined)
  if (!res.ok) {
    const parsed = ErrorEnvelopeSchema.safeParse(json)
    throw new ApiError(
      res.status,
      parsed.success
        ? parsed.data
        : {
            code: "UNKNOWN_ERROR",
            messageKey: "errors.unknown",
            correlationId: res.headers.get("x-correlation-id") ?? "unknown",
            retryable: res.status >= 500,
          }
    )
  }
  return schema.parse(json)
}
